// Import necessary components
import React, { useContext } from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Box,
} from "@mui/material";
import "./SideBar.css";
import {
  Home,
  Category,
  ShoppingCart,
  Menu,
  Support,
  Margin,
} from "@mui/icons-material";
import SegmentRoundedIcon from "@mui/icons-material/SegmentRounded";
import { useState } from "react";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import ViewInArIcon from "@mui/icons-material/ViewInAr";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import PinDropOutlinedIcon from "@mui/icons-material/PinDropOutlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import FavoriteBorderOutlinedIcon from "@mui/icons-material/FavoriteBorderOutlined";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import HeadphonesOutlinedIcon from "@mui/icons-material/HeadphonesOutlined";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { NavLink } from "react-router-dom";
import { useLocation } from "react-router-dom";
import AnimationIcon from "@mui/icons-material/Animation";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import ListItemButton from "@mui/material/ListItemButton";
import MuiDrawer from "@mui/material/Drawer";
import MuiAppBar from "@mui/material/AppBar";
import { styled, useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import useMediaQuery from "@mui/material/useMediaQuery";
import wallet from "../../Img/wallet.png";
import stake_portfolio from "../../Img/stake_portfolio.png";
import withdraw from "../../Img/withdraw.png";
import history from "../../Img/history.png";
import { green } from "@mui/material/colors";
import { ThemeContext } from "../DarkMode/ThemeProvider";
import MenuIcon from "@mui/icons-material/Menu";
import HistoryIcon from "@mui/icons-material/History";

const drawerWidth = 250;

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawers = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

const SideBar = () => {
  const { isLightMode, handleDarkmode } = useContext(ThemeContext);

  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up("lg"));

  const [open, setOpen] = useState(true);

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const currentLocation = useLocation(); // Renamed to avoid conflict
  const currentPath = currentLocation.pathname;
  const handleDrawerClose = () => {
    setOpen(false);
  };

  const [expanded, setExpanded] = useState(null);

  // const activeFil = loaction.pathname.toLocaleLowerCase()
  // console.log(activeFil, 'locat');

  // State to manage mobile drawer toggle
  const [mobileOpen, setMobileOpen] = useState(false);

  // SideBar items
  const [routes, setRoutes] = useState([
    {
      name: "Dashboard",
      svg: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M3.33203 9.99967V16.6663H7.91536V11.6663H12.082V16.6663H16.6654V9.99967L9.9987 3.33301L3.33203 9.99967Z"
            fill="#929AA5"
          ></path>
        </svg>
      ),
      path: "/dashboard",
    },
    {
      name: "Account",
      svg: (
        <svg
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M10.332 3.66634C10.332 4.5504 9.98084 5.39824 9.35572 6.02336C8.7306 6.64849 7.88275 6.99967 6.9987 6.99967C6.11464 6.99967 5.2668 6.64849 4.64167 6.02336C4.01655 5.39824 3.66536 4.5504 3.66536 3.66634C3.66536 2.78229 4.01655 1.93444 4.64167 1.30932C5.2668 0.684197 6.11464 0.333008 6.9987 0.333008C7.88275 0.333008 8.7306 0.684197 9.35572 1.30932C9.98084 1.93444 10.332 2.78229 10.332 3.66634ZM3.66536 8.66634C2.78131 8.66634 1.93346 9.01753 1.30834 9.64265C0.683221 10.2678 0.332031 11.1156 0.332031 11.9997V13.6663H13.6654V11.9997C13.6654 11.1156 13.3142 10.2678 12.6891 9.64265C12.0639 9.01753 11.2161 8.66634 10.332 8.66634H3.66536Z"
            fill="#929AA5"
          ></path>
        </svg>
      ),
      path: "#",
      children: [
        { name: "User Profile", path: "/profile" },
        // { name: "Identity Verification", path:"/identity-verification" },
        { name: "KYC Verification", path: "/Kycverify" },
        { name: "Security", path: "/Security" },
      ],
    },
    {
      name: "Assets",
      svg: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M17.5 4.74913H2.20563C2.02303 4.74913 1.875 4.6011 1.875 4.4185C1.875 4.24629 2.00718 4.10292 2.17882 4.08896L15.045 3.04245C15.3574 3.01704 15.625 3.26373 15.625 3.57718C15.625 3.87348 15.8652 4.11367 16.1615 4.11367H16.5625C17.0803 4.11367 17.5 3.69394 17.5 3.17617V2.20731C17.5 0.809316 16.3862 -0.171823 15.0262 0.0251674L2.475 1.84765C1.11375 2.04591 0 3.35113 0 4.74913L0 17.4582C0 18.1323 0.263392 18.7788 0.732233 19.2555C1.20107 19.7322 1.83696 20 2.5 20H17.5C18.163 20 18.7989 19.7322 19.2678 19.2555C19.7366 18.7788 20 18.1323 20 17.4582V7.29094C20 6.61681 19.7366 5.97029 19.2678 5.49361C18.7989 5.01692 18.163 4.74913 17.5 4.74913ZM15.625 13.6531C15.3787 13.653 15.1348 13.6036 14.9073 13.5077C14.6797 13.4118 14.473 13.2712 14.2989 13.0941C14.1248 12.917 13.9867 12.7067 13.8925 12.4753C13.7984 12.2439 13.7499 11.9959 13.75 11.7455C13.7501 11.495 13.7987 11.2471 13.893 11.0157C13.9873 10.7844 14.1256 10.5742 14.2998 10.3972C14.474 10.2202 14.6808 10.0798 14.9084 9.98402C15.136 9.88826 15.3799 9.83902 15.6263 9.8391C16.1237 9.83927 16.6007 10.0403 16.9523 10.3981C17.304 10.7558 17.5014 11.241 17.5012 11.7467C17.5011 12.2525 17.3033 12.7375 16.9515 13.095C16.5996 13.4525 16.1224 13.6533 15.625 13.6531Z"
            fill="#929AA5"
          />
        </svg>
      ),
      path: "#",
      children: [
        { name: "Wallet", path: "/assets" },
        // { name: "Reward", path: "/rewards" },
        // { name: "Referral", path: "/referral" },
      ],
    },
    // {
    //   name: "Stake Portfolio",
    //   svg: <img src={stake_portfolio} />,
    //   path: "/stake",
    // },
    // {
    //   name: "Rewards Hub",
    //   svg: (
    //     <svg
    //       width="16"
    //       height="12"
    //       viewBox="0 0 16 12"
    //       fill="none"
    //       xmlns="http://www.w3.org/2000/svg"
    //     >
    //       <path
    //         fillRule="evenodd"
    //         clipRule="evenodd"
    //         d="M0.5 0.166016H15.5V3.49935C14.837 3.49935 14.2011 3.76274 13.7322 4.23158C13.2634 4.70042 13 5.33631 13 5.99935C13 6.66239 13.2634 7.29827 13.7322 7.76712C14.2011 8.23596 14.837 8.49935 15.5 8.49935V11.8327H0.5V8.49935C1.16304 8.49935 1.79893 8.23596 2.26777 7.76712C2.73661 7.29827 3 6.66239 3 5.99935C3 5.33631 2.73661 4.70042 2.26777 4.23158C1.79893 3.76274 1.16304 3.49935 0.5 3.49935V0.166016ZM10.9167 2.24935H8.83333V9.74935H10.9167V2.24935Z"
    //         fill="#929AA5"
    //       />
    //     </svg>
    //   ),
    //   path: "/reward-hub",
    // },
    {
      name: "Deposit",
      svg: (
        <svg
          width="18"
          height="16"
          viewBox="0 0 18 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M17.3302 4.97927H13.8013C12.1591 4.98216 10.8287 6.27929 10.8258 7.88047C10.8235 9.48527 12.1554 10.7882 13.8013 10.7903H17.3332V11.0453C17.3332 13.8446 15.6361 15.5 12.7643 15.5H5.23613C2.36354 15.5 0.666504 13.8446 0.666504 11.0453V4.94822C0.666504 2.14885 2.36354 0.5 5.23613 0.5H12.7613C15.6332 0.5 17.3302 2.14885 17.3302 4.94822V4.97927ZM4.61613 4.97277H9.31613H9.3191H9.32502C9.67688 4.97133 9.96132 4.69182 9.95984 4.34804C9.95836 4.00498 9.67095 3.72765 9.3191 3.72909H4.61613C4.2665 3.73054 3.9828 4.00715 3.98132 4.34876C3.97984 4.69182 4.26428 4.97133 4.61613 4.97277Z"
            fill="#707A8A"
          />
        </svg>
      ),
      path: "/deposit",
    },
    {
      name: "Withdraw",
      svg: (
        <svg
          width="16"
          height="14"
          viewBox="0 0 16 14"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g clip-path="url(#clip0_77_10123)">
            <path
              d="M2.80326 8.23548C2.80326 6.54941 2.80499 4.86157 2.7998 3.1755C2.7998 3.02464 2.83781 2.99091 2.97772 2.99624C3.30245 3.00689 3.62718 3.00866 3.95192 2.99624C4.10392 2.99091 4.15056 3.02641 4.14883 3.19147C4.14192 5.2893 4.14537 7.3889 4.14537 9.48673C4.14537 9.76537 4.14365 10.044 4.14537 10.3209C4.1471 10.5747 4.2421 10.6812 4.48911 10.7167C5.1783 10.8143 5.60149 11.2473 5.71031 11.9608C5.75176 12.2395 5.84504 12.3247 6.11795 12.3247C7.37543 12.3247 8.63118 12.3247 9.88865 12.3247C10.1616 12.3247 10.2548 12.2377 10.2963 11.959C10.4017 11.2544 10.8266 10.8161 11.502 10.7167C11.7766 10.6759 11.8612 10.5765 11.863 10.2854C11.863 8.98801 11.863 7.69062 11.863 6.395C11.863 5.32124 11.8664 4.24571 11.8595 3.17195C11.8595 3.01754 11.9027 2.98914 12.0409 2.99269C12.3708 3.00334 12.7024 3.00334 13.0323 2.99269C13.174 2.98737 13.2103 3.02286 13.2103 3.17195C13.2051 5.68863 13.2068 8.20354 13.2068 10.7202C13.2068 11.5721 13.2068 12.424 13.2068 13.276C13.2068 13.5919 13.0945 13.709 12.7853 13.709C9.60019 13.709 6.41505 13.709 3.2299 13.709C2.92072 13.709 2.80844 13.5936 2.80844 13.276C2.80844 11.5952 2.80844 9.91446 2.80844 8.23194L2.80326 8.23548Z"
              fill="#707A8A"
            />
            <path
              d="M8.01452 0.290222C10.3654 0.290222 12.7162 0.290222 15.0671 0.290222C15.2416 0.290222 15.4143 0.306195 15.5732 0.386061C15.8478 0.526272 16.0016 0.742799 16.0016 1.07114C15.9981 2.1236 16.0016 3.17429 16.0016 4.22676C16.0016 4.68111 15.7079 5.01122 15.264 5.0272C14.8253 5.04317 14.3865 5.03252 13.9478 5.03607C13.8804 5.03607 13.8545 5.0201 13.8545 4.94378C13.858 4.32614 13.8563 3.70851 13.8545 3.09088C13.8545 3.01988 13.8753 2.99326 13.9478 2.99504C14.0825 3.00036 14.219 3.00036 14.3537 2.99504C14.5506 2.98794 14.6905 2.8495 14.6905 2.66492C14.6905 2.48034 14.5524 2.3419 14.3554 2.32948C14.3174 2.32771 14.2794 2.32948 14.2414 2.32948C10.0821 2.32948 5.92276 2.32948 1.76169 2.32948C1.70814 2.32948 1.65287 2.32593 1.59932 2.33658C1.43177 2.36675 1.31431 2.51051 1.32122 2.67557C1.32813 2.8495 1.45595 2.98616 1.63387 2.99504C1.77378 3.00213 1.91542 3.00213 2.05533 2.99504C2.13478 2.99149 2.15551 3.01988 2.15551 3.09975C2.15206 3.71206 2.15206 4.32437 2.15551 4.93668C2.15551 5.0201 2.12787 5.03784 2.0536 5.03607C1.62523 5.03252 1.19686 5.04139 0.770214 5.03074C0.307297 5.01832 0.00674667 4.68998 0.00674667 4.21966C0.00674667 3.19559 0.0240197 2.17152 -0.000162543 1.14746C-0.013981 0.574192 0.41439 0.311519 0.801306 0.291996C0.892853 0.286672 0.986127 0.290222 1.07767 0.290222C3.39053 0.290222 5.70339 0.290222 8.01625 0.290222H8.01452Z"
              fill="#707A8A"
            />
            <path
              d="M8.00617 8.68329C6.47233 8.68506 5.22694 7.4143 5.21831 5.83827C5.20967 4.26223 6.46369 2.96662 7.99926 2.96485C9.53484 2.96307 10.7906 4.25336 10.7871 5.83117C10.7837 7.4072 9.54174 8.68151 8.00617 8.68329Z"
              fill="#707A8A"
            />
            <path
              d="M6.81958 7.02691C6.82994 6.95592 6.83167 6.9222 6.83339 6.908C6.83512 6.89203 6.83858 6.86363 6.8403 6.84588C6.85412 6.69502 6.89039 6.4004 6.92494 6.175C6.95085 6.0064 7.01649 5.68161 7.24104 5.37811C7.31358 5.2805 7.39822 5.19353 7.50013 5.12786C7.12531 5.67096 7.05967 6.27972 7.1564 6.92042C7.17367 6.80151 7.18922 6.68082 7.20649 6.56191C7.25486 6.24067 7.34295 5.93363 7.51913 5.66031C7.64004 5.47218 7.78514 5.30889 7.99414 5.22015C8.02869 5.20596 8.06496 5.19708 8.10123 5.18643C7.90259 5.41361 7.74023 5.65676 7.62795 5.93363C7.51741 6.2105 7.46386 6.49802 7.48631 6.79796C7.53122 6.69857 7.56922 6.59741 7.60895 6.49802C7.72123 6.22115 7.85423 5.96025 8.07187 5.75437C8.14442 5.68693 8.22042 5.62481 8.32751 5.58399C8.0166 5.99752 7.79377 6.51932 7.82659 6.75537L7.86978 6.73939C7.89568 6.73052 7.92159 6.71987 7.9475 6.70745C8.16342 6.60451 8.33615 6.4501 8.48297 6.26019C8.55206 6.17145 8.61942 6.08271 8.69888 6.00285C8.78697 5.91411 8.88197 5.91766 8.95625 6.01527C9.01325 6.08981 9.06161 6.17855 9.08925 6.26907C9.12898 6.39685 9.14798 6.53174 9.17562 6.66663C9.26889 6.61516 9.34144 6.53706 9.36562 6.43058C9.39153 6.32054 9.4088 6.20517 9.40016 6.09336C9.3898 5.95848 9.39498 5.82182 9.41226 5.6887C9.46235 5.3018 9.28098 5.02492 8.91479 4.91844C8.75761 4.87229 8.5987 4.85987 8.43633 4.86519C8.41042 4.86519 8.37242 4.85277 8.36033 4.83325C8.22042 4.62204 8.02005 4.55992 7.78686 4.59187C7.73505 4.59897 7.60723 4.61494 7.45522 4.69481C7.1754 4.84034 7.02858 5.08349 6.97158 5.18111C6.55184 5.89103 6.78848 6.97899 6.81439 7.02691H6.81958ZM9.16698 5.55559C9.16698 5.59286 9.15143 5.62304 9.13243 5.62126C9.11343 5.61949 9.09961 5.59109 9.10134 5.55382C9.10307 5.51655 9.11689 5.48638 9.13589 5.48815C9.15489 5.48993 9.16871 5.51832 9.16698 5.55559Z"
              fill="white"
            />
          </g>
          <defs>
            <clipPath id="clip0_77_10123">
              <rect
                width="16"
                height="13.4194"
                fill="white"
                transform="translate(0 0.291016)"
              />
            </clipPath>
          </defs>
        </svg>
      ),
      path: "/withdraw",
    },
    {
      name: "History",
      svg: <HistoryIcon />,
      path: "/history",
    },
  ]);

  // Toggle drawer function
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // SideBar content
  const drawerContent = (
    <Box sx={{ width: 245 }} role="presentation" onClick={handleDrawerToggle}>
      <List className="listmui sidetwo-cnt">
        <div
          className="close-icon-side"
          style={{ display: matches ? "none" : "block" }}
          onClick={() => {
            handleDrawerToggle();
          }}
        >
          <div className="display-4 gap-5 p-10">
            <CloseIcon style={{ fontSize: "22px", color: "var(--clr-font)" }} />
          </div>
        </div>
        <>
          {routes.map((row, index) => (
            <div key={index} style={{ margin: "14px 0" }}>
              <NavLink
                className={({ isActive }) =>
                  isActive ? "active" : expanded === index ? "active nobg" : ""
                }
                to={row.path === "#" ? "javascript:void(0);" : row.path}
                // className={currentPath === row.path ? "active" : ""}
                // style={{ background:expanded ? "green" : "" }}
                onClick={() => {
                  if (row.children) {
                    // Toggle dropdown for this route
                    setExpanded(expanded === index ? null : index);
                  } else {
                    setExpanded(null); // Close dropdown if navigating elsewhere
                  }
                }}
              >
                <ListItem disablePadding sx={{ display: "block" }}>
                  <ListItemButton
                    sx={{
                      minHeight: 48,
                      justifyContent: expanded === index ? "initial" : "center",
                      px: 2.5,
                    }}
                    className="icobtn"
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: expanded === index ? 2 : "auto",
                        justifyContent: "center",
                      }}
                    >
                      {row.svg}
                    </ListItemIcon>
                    <ListItemText
                      className="routetext"
                      primary={row.name}
                    // sx={{ opacity: expanded === index ? 1 : 0 }}
                    />
                    {row.children && (
                      <ListItemIcon style={{ justifyContent: "end" }}>
                        {expanded === index ? (
                          <svg
                            width="10"
                            height="8"
                            viewBox="0 0 10 8"
                            fill="#566FFF"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M5.09224 4.14875L8.33391 0.90625L9.80724 2.37958L5.09307 7.09375L3.61974 5.62042L0.378906 2.37958L1.85224 0.90625L5.09307 4.14792L5.09224 4.14875Z"
                              fill="#566FFF"
                            ></path>
                          </svg>
                        ) : (
                          <svg
                            width="10"
                            height="8"
                            viewBox="0 0 10 8"
                            fill="#566FFF"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M5.09224 4.14875L8.33391 0.90625L9.80724 2.37958L5.09307 7.09375L3.61974 5.62042L0.378906 2.37958L1.85224 0.90625L5.09307 4.14792L5.09224 4.14875Z"
                              fill="#566FFF"
                            ></path>
                          </svg>
                        )}
                      </ListItemIcon>
                    )}
                  </ListItemButton>
                </ListItem>
              </NavLink>

              {row.children && expanded === index && (
                <div className="dropdown">
                  {row.children.map((child, childIndex) => (
                    <NavLink
                      key={childIndex}
                      className={({ isActive }) =>
                        isActive ? "active dropdown-item" : "dropdown-item"
                      }
                      to={child.path}
                      onClick={() => setExpanded(null)}
                    >
                      {child.name}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          ))}
        </>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", justifyContent: { xs: "end" } }}>
      {/* Drawer for mobile */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile
        }}
        sx={{
          display: { xs: "block", lg: "none" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: 250,
            backgroundColor: "var(--clr-bcg)",
          },
        }}
        className="side_bar"
      >
        {drawerContent}
      </Drawer>

      {/* Persistent drawer for larger screens */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: "none", lg: "block" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: "auto",
            top: "67px",
            borderRadius: "0",
            border: "none",
            position: "unset",
            zIndex: "1",
            backgroundColor: "var(--clr-bcg)",
          },
        }}
        className="side_bar"
        open
      >
        {drawerContent}
      </Drawer>
    </Box>
  );
};

export default SideBar;
